import { useRouteError, isRouteErrorResponse, useNavigate, Link } from 'react-router-dom'
import { ErrorState } from '@/components/common/ErrorState'
import { classifyError } from '@/lib/errorKind'

/**
 * Fallback UI when a route throws. Used as `errorElement` in the router.
 * Handles both thrown exceptions and router-thrown responses (404s from loaders).
 */
export function RouteErrorFallback(): React.ReactElement {
  const error = useRouteError()
  const navigate = useNavigate()

  // Router Responses aren't Errors, so the axios-shaped classifier can't read them.
  const routeResponse = isRouteErrorResponse(error) ? error : null
  const classified = routeResponse ? null : classifyError(error, 'crash')
  const showDashboardLink = routeResponse
    ? true
    : classified?.kind !== 'unauthorized'

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <ErrorState
        error={error}
        fallbackKind="crash"
        size="page"
        title={
          routeResponse
            ? routeResponse.status === 404
              ? 'Page not found'
              : `Request failed (${routeResponse.status})`
            : undefined
        }
        description={
          routeResponse?.status === 404
            ? 'The page you were looking for has moved or no longer exists.'
            : undefined
        }
        onRetry={routeResponse?.status === 404 ? undefined : () => navigate(0)}
        className="w-full max-w-lg"
        action={
          showDashboardLink && (
            <Link
              to="/dashboard"
              className="inline-flex h-8 min-h-8 items-center justify-center rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 transition-[background-color,scale] duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 active:scale-[0.96] dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              Go to dashboard
            </Link>
          )
        }
      />
    </div>
  )
}

RouteErrorFallback.displayName = 'RouteErrorFallback'
